import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { useEventCallback } from '@mui/material/utils';
import { throttle } from 'throttle-debounce';
import { useDocumentZoom, useDocumentZoomActions } from '@/pages/document/DocumentZoom';
import type { OcrWordsSelectionGesture } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/ocrWordsSelectionGesture';

type Options = {
  /** The pages list; its scroll is the vertical part of the page drag. */
  scrollElement: HTMLElement | null;
};

type ClientPoint = Pick<PointerEvent, 'clientX' | 'clientY'>;

/** The page's sideways place, kept between drags. */
type Pan = { x: number; y: number; shiftPercent: number };

/** One press on the page, from the press to the release; Ctrl or Cmd at the press makes it a selection. */
type Gesture = { pointerId: number; pressX: number; pressY: number; isCaptured: boolean } & (
  | { kind: 'pan'; start: { x: number; y: number } }
  | { kind: 'selection' }
);

const CAPTURE_DISTANCE = 4;
const WHEEL_ZOOM_STEP = 0.05;

/** The page and its status badge move sideways together: both read this custom property. */
export const PAGE_PAN_X = '--page-pan-x';

export const usePageGestures = ({ scrollElement }: Options) => {
  const scale = useDocumentZoom();
  const { zoomBy } = useDocumentZoomActions();
  const rootRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const selectionGesture = useRef<OcrWordsSelectionGesture | null>(null);
  // Written on every move without a render.
  const pan = useRef<Pan>({ x: 0, y: 0, shiftPercent: 50 });
  const gesture = useRef<Gesture | null>(null);
  const lastPointer = useRef<ClientPoint | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // The page lies left to right in every language (its row is LTR), so it moves only to the left of its start.
  // How far: the part of the zoomed page that does not fit in its row.
  const maxPanX = () => Math.max((pageRef.current?.offsetWidth ?? 0) - (rootRef.current?.clientWidth ?? 0), 0);
  const applyPanX = () => rootRef.current?.style.setProperty(PAGE_PAN_X, `${pan.current.x}px`);
  const panTo = (x: number) => {
    const p = pan.current;
    const shift = maxPanX();
    p.x = Math.max(-shift, Math.min(x, 0));
    // A page that fits in its row has no sideways room: its place stays as it was.
    if (shift > 0) p.shiftPercent = Math.max(Math.min(100, (100 / shift) * Math.abs(p.x)), 0);
  };
  const setGrabbing = (isGrabbing: boolean) =>
    pageRef.current?.style.setProperty('cursor', isGrabbing ? '-webkit-grabbing' : null);

  // A zoom keeps the page's sideways place as a share of how far it can move; back at 100% it is centred.
  const previousScale = useRef(scale);
  useLayoutEffect(() => {
    if (previousScale.current === scale) return;
    previousScale.current = scale;
    const p = pan.current;
    if (scale === 1) p.shiftPercent = 50;
    p.x = -maxPanX() * (p.shiftPercent / 100);
    applyPanX();
  });

  const registerSelectionGesture = useCallback((selection: OcrWordsSelectionGesture) => {
    selectionGesture.current = selection;
    return () => {
      if (selectionGesture.current === selection) selectionGesture.current = null;
    };
  }, []);

  // A portal's events bubble to the page through React, but its elements are not inside the page.
  const isPageTarget = (target: EventTarget) => target instanceof Node && !!pageRef.current?.contains(target);

  // From the top left corner of the page's content, wherever the pointer is over the page or its layers.
  const pagePoint = ({ clientX, clientY }: ClientPoint) => {
    const page = pageRef.current;
    if (!page) return { x: 0, y: 0 };
    const { left, top } = page.getBoundingClientRect();
    return { x: clientX - left - page.clientLeft, y: clientY - top - page.clientTop };
  };

  const movePage = () => {
    if (!pageRef.current || gesture.current?.kind !== 'pan') return;
    applyPanX();
    scrollElement?.scrollTo(0, pan.current.y);
  };

  const endGesture = () => {
    if (gesture.current?.kind === 'selection') {
      selectionGesture.current?.end();
      setIsSelecting(false);
    }
    gesture.current = null;
    setGrabbing(false);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // A press that a drag source took (dnd-kit marks it) belongs to that drag.
    if (event.button !== 0 || 'dndKit' in event.nativeEvent || !isPageTarget(event.target)) return;
    const press = { pointerId: event.pointerId, pressX: event.clientX, pressY: event.clientY, isCaptured: false };
    if ((event.metaKey || event.ctrlKey) && selectionGesture.current) {
      gesture.current = { ...press, kind: 'selection' };
      setIsSelecting(true);
      selectionGesture.current.start(pagePoint(event));
      return;
    }
    const p = pan.current;
    gesture.current = {
      ...press,
      kind: 'pan',
      start: { x: p.x ? event.clientX - p.x : event.clientX, y: (scrollElement?.scrollTop ?? 0) + event.clientY },
    };
    selectionGesture.current?.clear();
    setGrabbing(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (isPageTarget(event.target)) lastPointer.current = { clientX: event.clientX, clientY: event.clientY };
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    // Released outside the page before the capture: the page never got the release.
    if (event.buttons === 0) {
      endGesture();
      return;
    }
    if (
      !current.isCaptured &&
      Math.hypot(event.clientX - current.pressX, event.clientY - current.pressY) >= CAPTURE_DISTANCE
    ) {
      // Not on the press: a capture there sends the click to the page, and the buttons on it lose their click.
      pageRef.current?.setPointerCapture(event.pointerId);
      current.isCaptured = true;
    }
    if (current.kind === 'selection') {
      selectionGesture.current?.move(pagePoint(event));
      return;
    }
    panTo(event.clientX - current.start.x);
    pan.current.y = current.start.y - event.clientY;
    window.requestAnimationFrame(movePage);
  };

  // The statement grid opens where the pointer last was over the page; read only when it opens.
  const gridStartY = useEventCallback(() => (lastPointer.current ? pagePoint(lastPointer.current).y : 0));

  const wheelStep = useRef(0);
  const zoomByWheel = useMemo(() => throttle(200, false, () => zoomBy(wheelStep.current)), [zoomBy]);

  // A sideways wheel (a touchpad swipe) moves a zoomed page like a drag. Taken from the browser when it is mostly
  // sideways, so the browser does not read it as a swipe back in history; the up and down part of a slanted swipe
  // still scrolls the list.
  const panByWheel = useEventCallback((event: WheelEvent) => {
    if (event.deltaX === 0 || maxPanX() === 0) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      event.preventDefault();
      scrollElement?.scrollBy(0, event.deltaY);
    }
    panTo(pan.current.x - event.deltaX);
    applyPanX();
  });

  // React listens to the wheel passively, so only a native listener can keep Ctrl+wheel (and a trackpad pinch)
  // from zooming the whole browser window. A portal's wheel never reaches it: portals are not inside the page.
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) {
        panByWheel(event);
        return;
      }
      // A sideways swipe has no up or down to zoom by.
      if (event.deltaY === 0) return;
      event.preventDefault();
      wheelStep.current = -Math.sign(event.deltaY) * WHEEL_ZOOM_STEP;
      zoomByWheel();
    };
    page.addEventListener('wheel', onWheel, { passive: false });
    return () => page.removeEventListener('wheel', onWheel);
  }, [zoomByWheel, panByWheel]);

  return {
    rootRef,
    pageRef,
    isSelecting,
    gridStartY,
    registerSelectionGesture,
    pageHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endGesture,
      onPointerCancel: endGesture,
      onLostPointerCapture: endGesture,
    },
  };
};
